import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Plan, FetchFrequency } from '../../types/registration'
import type { RegistrationFormState, Step1Data, Step2Data, Step3Data } from '../../types/registration'
import { submitRegistration, checkEmailAvailability } from '../../services/registrationApi'
import StepIndicator from './StepIndicator'
import Step1Account from './Step1Account'
import Step2BusinessProfile from './Step2BusinessProfile'
import Step3Location from './Step3Location'

// ─── Validation helpers ───────────────────────────────────────────────────────

const EMAIL_RE = /^[a-zA-Z0-9][a-zA-Z0-9._+-]*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/

function validateStep1(data: Step1Data): Partial<Record<keyof Step1Data, string>> {
  const errors: Partial<Record<keyof Step1Data, string>> = {}

  if (!data.full_name.trim()) {
    errors.full_name = 'Full name is required.'
  }

  if (!data.email.trim()) {
    errors.email = 'Email address is required.'
  } else if (!EMAIL_RE.test(data.email.trim())) {
    errors.email = 'Please enter a valid email address.'
  }

  if (!data.password) {
    errors.password = 'Password is required.'
  } else if (data.password.length > 72) {
    errors.password = 'Password must be 72 characters or fewer.'
  } else if (
    data.password.length < 8 ||
    !/[A-Z]/.test(data.password) ||
    !/[a-z]/.test(data.password) ||
    !/[0-9]/.test(data.password)
  ) {
    errors.password =
      'Password must be at least 8 characters and include an uppercase letter, a lowercase letter, and a digit.'
  }

  if (!data.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password.'
  } else if (data.password !== data.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.'
  }

  return errors
}

function validateStep2(data: Step2Data): Partial<Record<keyof Step2Data, string>> {
  const errors: Partial<Record<keyof Step2Data, string>> = {}

  if (!data.business_name.trim()) {
    errors.business_name = 'Business name is required.'
  }

  if (!data.industry) {
    errors.industry = 'Please select an industry.'
  }

  if (!data.brand_voice.trim()) {
    errors.brand_voice = 'Brand voice is required.'
  } else if (data.brand_voice.length > 500) {
    errors.brand_voice = 'Brand voice must be 500 characters or fewer.'
  }

  if (data.website_url && data.website_url.trim()) {
    const url = data.website_url.trim()
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      errors.website_url = 'Website URL must start with http:// or https://'
    }
  }

  if (!data.plan) {
    errors.plan = 'Please select a plan.'
  }

  if (!data.fetch_frequency) {
    errors.fetch_frequency = 'Please select a fetch frequency.'
  }

  return errors
}

function validateStep3(data: Step3Data): Partial<Record<keyof Step3Data, string>> {
  const errors: Partial<Record<keyof Step3Data, string>> = {}

  if (!data.location_name.trim()) {
    errors.location_name = 'Location name is required.'
  }

  if (!data.location_address.trim()) {
    errors.location_address = 'Street address is required.'
  }

  if (!data.notification_email.trim()) {
    errors.notification_email = 'Notification email is required.'
  } else if (!EMAIL_RE.test(data.notification_email.trim())) {
    errors.notification_email = 'Please enter a valid email address.'
  }

  return errors
}

// ─── Default form state ───────────────────────────────────────────────────────

const DEFAULT_FORM_STATE: RegistrationFormState = {
  // Step 1
  full_name: '',
  email: '',
  password: '',
  confirmPassword: '',
  // Step 2
  business_name: '',
  industry: '',
  brand_voice: 'friendly, professional, empathetic',
  website_url: '',
  plan: Plan.STARTER,
  fetch_frequency: FetchFrequency.EVERY_12_HOURS,
  // Step 3
  location_name: '',
  location_address: '',
  notification_email: '',
  google_credentials: undefined,
  // Honeypot — always empty for real users
  phone_number: '',
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RegistrationWizard() {
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState(1)
  const [formState, setFormState] = useState<RegistrationFormState>(DEFAULT_FORM_STATE)

  const [step1Errors, setStep1Errors] = useState<Partial<Record<keyof Step1Data, string>>>({})
  const [step2Errors, setStep2Errors] = useState<Partial<Record<keyof Step2Data, string>>>({})
  const [step3Errors, setStep3Errors] = useState<Partial<Record<keyof Step3Data, string>>>({})

  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)

  // ── Step data slices ──────────────────────────────────────────────────────

  const step1Data: Step1Data = {
    full_name: formState.full_name,
    email: formState.email,
    password: formState.password,
    confirmPassword: formState.confirmPassword,
  }

  const step2Data: Step2Data = {
    business_name: formState.business_name,
    industry: formState.industry,
    brand_voice: formState.brand_voice,
    website_url: formState.website_url,
    plan: formState.plan,
    fetch_frequency: formState.fetch_frequency,
  }

  const step3Data: Step3Data = {
    location_name: formState.location_name,
    location_address: formState.location_address,
    notification_email: formState.notification_email,
    google_credentials: formState.google_credentials,
    judge_me_api_token: formState.judge_me_api_token,
    judge_me_shop_domain: formState.judge_me_shop_domain,
  }


  // ── Navigation ────────────────────────────────────────────────────────────

  const handleStep1Next = async () => {
    const errors = validateStep1(step1Data)
    if (Object.keys(errors).length > 0) {
      setStep1Errors(errors)
      return
    }
    setStep1Errors({})

    setIsCheckingEmail(true)
    try {
      const available = await checkEmailAvailability(step1Data.email.trim())
      if (!available) {
        setStep1Errors({ email: 'This email is already registered. Please log in instead.' })
        return
      }
    } catch {
      // Network error — let the user proceed; the server will catch it at submit
    } finally {
      setIsCheckingEmail(false)
    }

    // Pre-fill notification_email from Step 1 email if not yet set
    setFormState((prev) => ({
      ...prev,
      notification_email: prev.notification_email || prev.email,
    }))

    setCurrentStep(2)
  }

  const handleStep2Next = () => {
    const errors = validateStep2(step2Data)
    if (Object.keys(errors).length > 0) {
      setStep2Errors(errors)
      return
    }
    setStep2Errors({})
    setCurrentStep(3)
  }

  const handleBack = () => {
    setCurrentStep((s) => Math.max(1, s - 1))
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleStep3Submit = async () => {
    const errors = validateStep3(step3Data)
    if (Object.keys(errors).length > 0) {
      setStep3Errors(errors)
      return
    }
    setStep3Errors({})
    setSubmitError(undefined)
    setIsSubmitting(true)

    try {
      const response = await submitRegistration(formState)
      localStorage.setItem('jwt_token', response.jwt_token)
      localStorage.setItem('email_verified', String(response.email_verified))
      localStorage.setItem('onboarding_completed', String(response.onboarding_completed))
      localStorage.setItem('customer_email', formState.email)
      navigate('/check-your-email', { state: { email: formState.email } })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown_error'

      if (message === 'email_already_registered') {
        setStep1Errors({ email: 'This email is already registered. Please log in instead.' })
        setCurrentStep(1)
      } else if (message === 'rate_limit_exceeded') {
        setSubmitError('Too many registration attempts. Please wait a while and try again.')
      } else {
        setSubmitError('Something went wrong. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const stepHeadings = ['Create your account', 'Business profile', 'Add your first location']
  const stepSubtitles = [
    'Get started with ReputeDesk in minutes.',
    'Tell us about your business and choose a plan.',
    'Set up your first location to start managing reviews.',
  ]

  return (
    <div className="min-h-screen bg-rd-bg font-dmSans relative overflow-hidden">
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px]"
        style={{ background: 'radial-gradient(ellipse, rgba(74,222,128,0.08) 0%, transparent 70%)' }}
      />

      {/* Nav */}
      <nav className="relative z-10 px-6 py-4 border-b border-white/[0.07]">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-rd-text no-underline hover:opacity-80 transition-opacity"
        >
          <img
            src={`${import.meta.env.BASE_URL}logo-icon.png`}
            alt="ReputeDesk"
            className="w-8 h-8 object-contain"
          />
          <span className="font-sora font-bold text-lg">ReputeDesk</span>
        </Link>
      </nav>

      {/* Content */}
      <div className="relative z-10 flex justify-center px-4 py-10 sm:py-14">
        <div className="w-full max-w-lg">
          {/* Heading */}
          <div className="text-center mb-7">
            <h1 className="font-sora font-bold text-2xl sm:text-[1.75rem] text-rd-text mb-2 tracking-tight">
              {stepHeadings[currentStep - 1]}
            </h1>
            <p className="text-rd-muted text-sm">{stepSubtitles[currentStep - 1]}</p>
          </div>

          <StepIndicator currentStep={currentStep} totalSteps={3} />

          {/* Honeypot — hidden from real users; bots fill it and get rejected by the backend */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <input
              type="text"
              name="phone_number"
              tabIndex={-1}
              autoComplete="off"
              value={formState.phone_number ?? ''}
              onChange={(e) => setFormState((prev) => ({ ...prev, phone_number: e.target.value }))}
            />
          </div>

          {/* Form card */}
          <div className="bg-rd-surface border border-white/[0.07] rounded-2xl p-6 sm:p-8 mt-5">
            {currentStep === 1 && (
              <Step1Account
                data={step1Data}
                onChange={(data) => setFormState((prev) => ({ ...prev, ...data }))}
                errors={step1Errors}
                onNext={handleStep1Next}
                isCheckingEmail={isCheckingEmail}
              />
            )}

            {currentStep === 2 && (
              <Step2BusinessProfile
                data={step2Data}
                onChange={(data) => setFormState((prev) => ({ ...prev, ...data }))}
                errors={step2Errors}
                onNext={handleStep2Next}
                onBack={handleBack}
              />
            )}

            {currentStep === 3 && (
              <Step3Location
                data={step3Data}
                onChange={(data) => setFormState((prev) => ({ ...prev, ...data }))}
                errors={step3Errors}
                onSubmit={handleStep3Submit}
                onBack={handleBack}
                isSubmitting={isSubmitting}
                submitError={submitError}
                plan={formState.plan}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
